import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Factory, Download, RotateCcw, Building, Wrench, DollarSign, AlertCircle, FileText } from "lucide-react";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const ScheduleFPreview = ({ calculationId }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [scheduleFData, setScheduleFData] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);

  useEffect(() => { if (calculationId) loadScheduleF(); }, [calculationId]);

  const loadScheduleF = async () => {
    setLoading(true); setError(null);
    try {
      const response = await axios.get(`${API}/com-schedule-f/${calculationId}`);
      setScheduleFData(response.data);
      if (response.data.generated_timestamp) setLastGenerated(new Date(response.data.generated_timestamp).getTime());
    } catch (e) {
      if (e.response?.status !== 404) setError(e.response?.data?.detail || "Error loading Schedule F data");
    } finally { setLoading(false); }
  };

  const handleGenerateScheduleF = async () => {
    setGenerating(true); setError(null); setSuccess(null);
    try {
      const response = await axios.post(`${API}/generate-schedule-f/${calculationId}`);
      if (response.data.status === 'success') {
        setSuccess("Schedule F generated successfully!");
        setTimeout(() => { loadScheduleF(); setSuccess(null); }, 1200);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error generating Schedule F");
    } finally { setGenerating(false); }
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value);
  };

  const formatDate = (timestamp) => timestamp ? new Date(timestamp).toLocaleString() : '';

  if (loading) return (
    <Card><CardContent className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div><p className="text-sm text-slate-600">Loading Schedule F data...</p></CardContent></Card>
  );

  return (
    <div className="space-y-6">
      {error && (<Alert className="border-red-200 bg-red-50"><AlertCircle className="h-4 w-4 text-red-600" /><AlertDescription className="text-red-700">{error}</AlertDescription></Alert>)}
      {success && (<Alert className="border-green-200 bg-green-50"><AlertCircle className="h-4 w-4 text-green-600" /><AlertDescription className="text-green-700">{success}</AlertDescription></Alert>)}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2"><Factory className="h-5 w-5 text-indigo-600" /><span>Schedule F - Facilities Capital Cost of Money</span></CardTitle>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={loadScheduleF} disabled={loading}><RotateCcw className="h-4 w-4 mr-2" />Refresh</Button>
              <Button onClick={handleGenerateScheduleF} disabled={generating || !calculationId} className="bg-indigo-600 hover:bg-indigo-700"><Download className="h-4 w-4 mr-2" />{generating ? "Generating..." : "Generate Schedule F"}</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2"><Factory className="h-4 w-4 text-indigo-600" /><span className="text-sm font-medium text-indigo-900">Schedule F Summary</span></div>
              <div className="text-lg font-bold text-indigo-600">{scheduleFData ? "Generated" : "Not Generated"}</div>
              {scheduleFData && (<div className="text-xs text-indigo-700 mt-1">{formatDate(scheduleFData.generated_timestamp)}</div>)}
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2"><FileText className="h-4 w-4 text-blue-600" /><span className="text-sm font-medium text-blue-900">Schedule F-1 Details</span></div>
              <div className="text-lg font-bold text-blue-600">{scheduleFData?.asset_details ? `${scheduleFData.asset_details.length} Assets` : "Not Generated"}</div>
              {scheduleFData?.total_com && (<div className="text-xs text-blue-700 mt-1">Total COM: {formatCurrency(scheduleFData.total_com)}</div>)}
            </div>
          </div>
        </CardContent>
      </Card>

      {scheduleFData ? (
        <Card>
          <CardHeader><CardTitle className="flex items-center justify-between"><div className="flex items-center space-x-2"><Factory className="h-5 w-5 text-indigo-600" /><span>Schedule F & F-1 - Cost of Money Computation</span></div><Badge variant="outline" className="text-xs">Generated: {formatDate(scheduleFData.generated_timestamp)}</Badge></CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg"><div className="text-sm text-slate-600 mb-1">Total Facilities NBV</div><div className="text-lg font-semibold">{formatCurrency(scheduleFData.summary?.total_facilities_nbv || 0)}</div></div>
              <div className="bg-slate-50 p-4 rounded-lg"><div className="text-sm text-slate-600 mb-1">Total Equipment NBV</div><div className="text-lg font-semibold">{formatCurrency(scheduleFData.summary?.total_equipment_nbv || 0)}</div></div>
              <div className="bg-slate-50 p-4 rounded-lg"><div className="text-sm text-slate-600 mb-1">Treasury Rate</div><div className="text-lg font-semibold">{((scheduleFData.summary?.treasury_rate || 0) * 100).toFixed(3)}%</div></div>
              <div className="bg-indigo-50 p-4 rounded-lg"><div className="text-sm text-indigo-600 mb-1">Total COM</div><div className="text-lg font-bold text-indigo-600">{formatCurrency(scheduleFData.total_com || 0)}</div></div>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold mb-4 text-slate-700">Schedule F-1: Asset-by-Asset COM Computation</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Net Book Value</TableHead>
                      <TableHead className="text-right">Treasury Rate</TableHead>
                      <TableHead className="text-right">COM Factor</TableHead>
                      <TableHead className="text-right">Computed COM</TableHead>
                      <TableHead className="text-right">Allowable %</TableHead>
                      <TableHead className="text-right">Allowable COM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleFData.asset_details?.map((asset, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          <div className="flex items-center space-x-2">{asset.asset_type === 'facility' ? (<Building className="h-4 w-4 text-indigo-600" />) : (<Wrench className="h-4 w-4 text-blue-600" />)}<span>{asset.asset_name}</span></div>
                        </TableCell>
                        <TableCell><Badge variant="outline" className={`text-xs ${asset.asset_type === 'facility' ? 'bg-indigo-50 text-indigo-700' : 'bg-blue-50 text-blue-700'}`}>{asset.asset_type === 'facility' ? 'Facility' : 'Equipment'}</Badge></TableCell>
                        <TableCell className="text-right">{formatCurrency(asset.net_book_value)}</TableCell>
                        <TableCell className="text-right">{(asset.treasury_rate * 100).toFixed(3)}%</TableCell>
                        <TableCell className="text-right">{(asset.com_factor * 100).toFixed(3)}%</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(asset.computed_com)}</TableCell>
                        <TableCell className="text-right">{(asset.allowable_percentage * 100).toFixed(1)}%</TableCell>
                        <TableCell className="text-right font-semibold text-indigo-600">{formatCurrency(asset.allowable_com)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card><CardContent className="text-center py-8"><Factory className="h-16 w-16 mx-auto mb-4 text-slate-300" /><h3 className="text-lg font-medium mb-2 text-slate-600">Schedule F Not Generated</h3><p className="text-sm text-slate-500 mb-4">Generate Schedule F to view COM calculations.</p><Button onClick={handleGenerateScheduleF} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700"><Download className="h-4 w-4 mr-2" />Generate Schedule F</Button></CardContent></Card>
      )}
    </div>
  );
};

export default ScheduleFPreview;